import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { formatKw } from "./connectors";

import type { StationListItem } from "./api";

type Props = {
  station: StationListItem;
  distanceM?: number;
};

export function StationCard({ station, distanceM }: Props) {
  const { t } = useTranslation();
  const counts = `${station.availableCount}/${station.chargerCount} disponibles`;
  const distance = distanceM !== undefined ? ` · ${formatDistance(distanceM)}` : "";
  const peakKw = station.plugs[0]?.power_kw;

  return (
    <Link
      to={`/stations/${station.id}`}
      className="group block rounded-lg border border-line bg-surface p-4 transition-all hover:border-line-strong"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold tracking-tight text-fg">
            {station.name}
          </h3>
          {station.address && (
            <p className="truncate text-xs text-fg-mute">{station.address}</p>
          )}
          <p className="num mt-2 text-xs text-fg-mute">{counts}{distance}</p>
        </div>
        <div className="flex min-w-0 shrink-0 flex-col items-end gap-1.5">
          {peakKw !== undefined && (
            <span className="num inline-flex items-center gap-1 rounded-full border border-volt/25 bg-volt/5 px-2.5 py-1 text-xs font-semibold text-volt-deep">
              <BoltGlyph className="text-volt-deep" />
              {formatKw(peakKw)} kW
            </span>
          )}
          {station.company && (
            <span className="max-w-[40vw] truncate rounded-full bg-surface-raised px-2.5 py-0.5 text-[11px] font-medium text-fg-mute">
              {station.company}
            </span>
          )}
        </div>
      </div>
      {station.tags.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5" aria-label={t("stations.tags_aria")}>
          {station.tags.map((tag) => (
            <li
              key={tag}
              className="rounded-full bg-surface-raised px-2.5 py-0.5 text-[11px] text-fg-mute"
            >
              {tag}
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

function BoltGlyph({ className = "" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      width="10"
      height="10"
      className={className}
      fill="currentColor"
    >
      <path d="M7 1 2 7h3l-1 4 5-6H6l1-4z" />
    </svg>
  );
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}
